import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import { type ExpenseItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { mapExpenseTypeDataToFields } from './mapExpenseItemFormSchemaToItemPayload'
import { type ExpenseItemUpdatePayload } from '../types/ExpenseItemUpdatePayload'

interface MapExpenseItemFormSchemaToUpdatePayloadContext {
  itemId: number
  expenseReportId: number
}

const formatTaxPercentage = (item: ExpenseItemFormSchema): string => {
  // 'saved' (custom) tax preserves the Tax entity's raw decimal string;
  // percent and dollar modes serialize as a fixed-precision decimal for
  // the V3 endpoint.
  if (item.taxMode === 'saved') {
    return item.taxPercentage || '0'
  }
  return Number.parseFloat(item.taxPercentage || '0').toFixed(8)
}

export const mapExpenseItemFormSchemaToUpdatePayload = (
  item: ExpenseItemFormSchema,
  { itemId, expenseReportId }: MapExpenseItemFormSchemaToUpdatePayloadContext
): ExpenseItemUpdatePayload => {
  let paymentType: number
  let creditcard: Nullable<number>

  if (item.reimbursable || item.paymentMethod?.id === OTHER_PAYMENT_OPTION_ID) {
    paymentType = ExpensePaymentTypes.OTHER
    creditcard = null
  } else {
    paymentType = item.paymentType
    creditcard = item.paymentMethod?.id ?? null
  }

  const computedSubtotal = Number.parseFloat(item.subtotal || '0')
  const computedTaxAmount = Number.parseFloat(item.taxAmount || '0')
  const computedTotal = Number.parseFloat(item.amount || '0')

  const isReclaimable = item.isReclaimableMileageTax === true

  // Custom dollar tax should not be treated as an override of a percentage calculation
  const isDollarTax = item.taxMode === 'dollar'
  const taxAmountValue = isDollarTax ? computedTaxAmount.toFixed(2) : undefined
  const taxAmountOverride =
    !isDollarTax && item.isTaxAmountOverridden
      ? computedTaxAmount.toFixed(2)
      : undefined

  return {
    id: itemId,
    merchant: item.merchant,
    account: item.account?.id ?? 0,
    expenseReport: expenseReportId,
    transDate: item.transactionDate,
    subtotal: Number.parseFloat(computedSubtotal.toFixed(2)),
    amount: computedTotal.toFixed(2),
    reimburse: String(item.reimbursable),
    expense_type: item.expenseType,
    expense_type_fields: mapExpenseTypeDataToFields(item.expenseTypeData) ?? {},
    mileage_preset_id: item.mileageRatePreset?.id ?? null,
    localCurrency: item.currency.id,
    currency_rate_final: item.currency.rate,
    tax_percentage: isReclaimable ? '0' : formatTaxPercentage(item),
    tax_amount: taxAmountValue,
    tax_amount_override: taxAmountOverride,
    tax_name: isReclaimable ? '' : item.taxName,
    tax_id: isReclaimable ? null : item.taxId,
    tax_is_inclusive: isReclaimable ? false : item.taxIsInclusive,
    paymentType,
    creditcard,
    attachment: item.attachment,
    notes: item.notes,
    exchange_rate_override: false,
    has_default_tax: false,
  }
}
